import React, { useState, useEffect } from "react";
import Sidebar from "../components/SideBar";
import { UserData } from "../Functions/firebaseFunction";
import { updateVendorProfile } from "../Functions/firebaseImageHandlerFunction";
import { db, storage, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import ProfileSection from "./SettingSection/ProfileSection";
import PaymentSection from "./SettingSection/PaymentSection";
import CustomAlert from "../components/CustomAlert";
import { handleStripeConnect } from "./SettingSection/StripeConnect";
import { FaInbox } from "react-icons/fa";
import useStore from "../store/useStore";

const Settings = () => {
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnectLoading, setIsConnectLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const userData = useStore((state) => state.userData);
  const setUserData = useStore((state) => state.setUserData);

  const [isLoading, setIsLoading] = useState(!userData);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [alert, setAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const [profileData, setProfileData] = useState({
    firstName: userData?.firstName || "",
    lastName: userData?.lastName || "",
    email: userData?.email || "",
    countryCode: userData?.countryCode || (userData?.mobile?.startsWith('+') ? userData.mobile.substring(0, userData.mobile.length - 10) : "+1"),
    mobile: userData?.mobile?.length > 10 ? userData.mobile.slice(-10) : (userData?.mobile || ""),
    city: userData?.city || "",
    state: userData?.state || "",
    zip: userData?.zip || "",
    headquarters: userData?.headquarters || "",
    profilePictureUrl: userData?.profilePictureUrl || "",
    isVerified: userData?.isVerified || false,
    stripeAccountId: userData?.stripeAccountId || "",
    stripeStatus: userData?.stripeStatus || "",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setIsAuthReady(true);
      else {
        setIsAuthReady(false);
        setIsLoading(false);
        setError("Please sign in to access your profile.");
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    const fetchUserData = async () => {
      if (!userData) setIsLoading(true);
      try {
        const data = await UserData();
        if (data) {
          setProfileData((prev) => ({ ...prev, ...data }));
          setUserData(data);
        }
        else setError("No user data found. Please complete your profile setup.");
      } catch {
        setError("Failed to fetch user data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [isAuthReady]);

  const handleInputChange = (field, value) => {
    if (field === "email") return;
    setProfileData((prev) => ({ ...prev, [field]: value }));
    setIsModified(true);
  };

  const handleProfileImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setProfileData((p) => ({ ...p, profilePictureUrl: event.target.result }));
      setProfilePictureFile(file);
      setIsModified(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!isModified) return;
    setIsSaving(true);
    try {
      await updateVendorProfile(
        db,
        storage,
        auth,
        { ...profileData, profilePictureFile },
        setError,
        setIsSaving,
        setUploadProgress
      );
      setIsModified(false);
      setAlert({
        show: true,
        type: "success",
        message: "Profile updated successfully!",
      });
    } catch {
      setAlert({
        show: true,
        type: "error",
        message: "Failed to update profile.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleStripeConnectWrapper = async () => {
    await handleStripeConnect(setAlert, setIsConnectLoading);
  };

  const handleSaveBankDetails = async () => {};

  // ✅ Realistic shimmer matching input layout
  const FormShimmer = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
      {/* Profile Image Row */}
      <div className="flex items-center mb-8 gap-6">
        <div className="w-24 h-24 rounded-full bg-gray-200" />
        <div className="flex-1 space-y-3">
          <div className="h-4 w-32 bg-gray-200 rounded" />
          <div className="h-3 w-20 bg-gray-200 rounded" />
        </div>
      </div>

      {/* Two-column row: First Name / Last Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        {[...Array(2)].map((_, i) => (
          <div key={i}>
            <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-10 w-full bg-gray-200 rounded" />
          </div>
        ))}
      </div>

      {/* Single field: Email */}
      <div className="mb-6">
        <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
        <div className="h-10 w-full bg-gray-200 rounded" />
      </div>

      {/* Two-column row: City / State */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        {[...Array(2)].map((_, i) => (
          <div key={i}>
            <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-10 w-full bg-gray-200 rounded" />
          </div>
        ))}
      </div>

      {/* Single: Headquarters */}
      <div className="mb-6">
        <div className="h-3 w-28 bg-gray-200 rounded mb-2" />
        <div className="h-10 w-full bg-gray-200 rounded" />
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <div className="h-10 w-36 bg-gray-200 rounded" />
      </div>
    </div>
  );

  const PaymentShimmer = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
      <div className="h-5 w-28 bg-gray-200 rounded mb-6" />
      {[...Array(2)].map((_, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {[...Array(2)].map((_, j) => (
            <div key={j}>
              <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
              <div className="h-10 w-full bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      ))}
      <div className="flex justify-end">
        <div className="h-10 w-36 bg-gray-200 rounded" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="ml-80 p-8 lg:p-10 min-h-screen">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Header (Always Visible) */}
          <div className="flex justify-between items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
              <p className="text-slate-500 mt-1 text-sm sm:text-base">
                Manage your profile, payments, and verification status.
              </p>
            </div>

          {!isLoading && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              {profileData.isVerified ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-full font-medium">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Verified
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-2 rounded-full font-medium">
                  <FaInbox className="text-yellow-600" />
                  Unverified
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {isLoading ? (
            <>
              <FormShimmer />
              <PaymentShimmer />
            </>
          ) : (
            <>
              <ProfileSection
                profileData={profileData}
                handleInputChange={handleInputChange}
                handleProfileImage={handleProfileImage}
                handleSave={handleSave}
                isModified={isModified}
                isSaving={isSaving}
                error={error}
                uploadProgress={uploadProgress}
              />
              <PaymentSection
                profileData={profileData}
                isSaving={isConnectLoading}
                handleStripeConnect={handleStripeConnectWrapper}
                handleInputChange={handleInputChange}
                handleSaveBankDetails={handleSaveBankDetails}
              />
            </>
          )}
        </div>
        </div>
      </main>

      {/* Alert */}
      <CustomAlert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert({ ...alert, show: false })}
      />
    </div>
  );
};

export default Settings;
